import { SetMirrorData, OptimisticJobTemplate } from '@type/job-add';
import {
  Dispatch, SetStateAction, useRef, useCallback, useState,
} from 'react';
import {
  Button, Label, ComboBox, RecordInterface,
} from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import { getFormattedJobTitle } from '@components/templates/job-add/edit/functions/jobTitleFunctions';
import { MagnifyingGlassIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { dataTestId } from '@services/data-test-id';

/** Props */
interface JobTitleProps {
  record: RecordInterface,
  setCurrentJobSlug: Dispatch<SetStateAction<string | undefined>>,
  optimisticJobTemplate: OptimisticJobTemplate,
  inputName?: string,
  isTitleEditable?: boolean,
  labelDescriptionClass?: string,
  setMirrorData?: SetMirrorData,
  apiTitle?: string,
}

/** Fake submit classes */
const fakeSubmit = {
  animation: 'transition-all duration-300',
  common: 'absolute right-1.75 top-1.75 z-1 items-center justify-center !p-0 size-9',
  visible: 'scale-100 delay-100',
  hidden: 'scale-0',
};

/**
 * @description Job Title component. To be used in a form.
 * - `record` - record function from useBjForm
 * - `setCurrentJobSlug` - setter for the new job slug
 * - `optimisticJobTemplate` - function to handle the template description (optimistically update with description from title search response)
 * - `inputName` - name of the input; default is 'title'
 * - `inActivePeriod` - whether the job is in an active period
 * - `labelDescriptionClass` - additional classes for the label description
 * - `setMirrorData` - function to update the mirror form state
 * - `apiTitle` - the title from the API
 *
 * **LOGIC**
 * - `template` - If the user selects a job with template, we use the description from the autocomplete response.
 * - `existing job` - If the user selects an existing job, we do a request with the job slug to get the full job.
 * - `else` - If the user selects a keyword or writes a new job title, we use the already fetched job template.
 */
export const JobTitle = (props: JobTitleProps) => {
  // Destructure props
  const {
    record,
    setCurrentJobSlug,
    optimisticJobTemplate,
    inputName = 'title',
    isTitleEditable = false,
    labelDescriptionClass = '',
    setMirrorData,
    apiTitle,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Helpers
  const selectedTitle = useRef<string | undefined>(undefined);
  const curentKeyNr = useRef<number>(0);
  const [inputVal, setInputVal] = useState<string>(apiTitle ?? '');
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Clear title
  // **************************
  const clearTitle = useCallback(() => {
    // Reset the job template. Clear the optimistically set updates.
    setCurrentJobSlug(undefined);
    // Add the key number to trigger a rerender
    optimisticJobTemplate({ key: `currentKey-${curentKeyNr.current}` }, true);
    // Reset the mirror data
    setMirrorData?.(undefined, true);
    // Increment the key number for the next reset
    curentKeyNr.current += 1;
  }, [optimisticJobTemplate, setCurrentJobSlug, setMirrorData]);

  // Render
  // **************************
  return (
    <Label
      required
      as="div"
      description={t('job.add.label.title')}
      descriptionClass={labelDescriptionClass}
    >
      <div className="relative">
        <MagnifyingGlassIcon
          className="absolute left-3 top-6 z-1 size-5 -translate-y-1/2 text-ink"
        />
        <ComboBox
          required
          placeholder={t('job.add.placeholder.title')}
          className="text-ellipsis pl-10 pr-18"
          buttonsContainerClass={`top-2 ${fakeSubmit.animation} ${isFocused ? 'right-11' : 'right-0.5 delay-100'}`}
          minLength={3}
          closeOnBlur
          options={{
            valueFromKey: 'name',
            showDropdownButton: isTitleEditable,
            showResetButton: isTitleEditable,
            currentFieldValue: setInputVal,
            apiList: {
              fetchUrl: '/v1/job-title-autocomplete',
              queryParams: (query: string) => ({
                term: query,
              }),
            },
          }}
          readOnly={!isTitleEditable}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            // If the user does not select an item, we update the mirror data with the custom job title.
            if (!selectedTitle.current) setMirrorData?.();
            // Clear the title if the input is empty
            if (!(inputVal && inputVal.length >= 3)) clearTitle();
          }}
          onInputSubmit={(_, e) => {
            // Blur the input on submit (keyboard enter)
            (e?.target as HTMLInputElement).blur();
          }}
          onSelectedItemChange={(item, inputRef) => {
            const selectedItem = item as ApiSchema<'JobContentModel'> | undefined;
            // Set the current selected title
            selectedTitle.current = selectedItem?.name;
            if (selectedItem) {
              // Clear the optimistically set updates (ie: template description).
              // By adding the title to jobTemplate like optimisticJobTemplate({ title: selectedItem?.name }, true),
              // we can reset the form via key (JSON.stringify(jobTemplate)).
              // Don't add the title for soft reset.
              optimisticJobTemplate({ title: selectedItem?.name }, true);
              // Set new job slug for fetching the job template
              setCurrentJobSlug(selectedItem?.slug);
              // If not an existing job, we set the mirror data with the selected item description.
              // For existing jobs, we do it in the form hook after fetching the full job.
              if (!selectedItem?.slug) setMirrorData?.();
            }
            // Blur the input on select. Make sure we actually select an item.
            if (selectedItem && inputRef && inputRef?.current) inputRef.current.blur();
          }}
          onClear={clearTitle}
          renderList={(
            item,
            index,
            listKey,
            getItemProps,
            listStyling,
            highlightedIndex,
          ) => {
            const titleItem = getFormattedJobTitle(item as ApiSchema<'JobContentModel'>);
            const highlightedClass = highlightedIndex === index ? 'text-white' : 'text-ink-medium';
            const hasSubtitle = titleItem.subtitle && titleItem.subtitle.length > 0;
            return (
              <li key={listKey} className={`${listStyling} items-start gap-2${hasSubtitle ? '' : ' !py-3.5'}`} {...getItemProps({ item, index })}>
                {titleItem.icon}
                <span className="flex shrink-0 flex-col">
                  <span>{titleItem.title}</span>
                  {hasSubtitle && (
                    <span className={`${highlightedClass} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                      {t(titleItem.subtitle)}
                    </span>
                  )}
                </span>
              </li>
            );
          }}
          {...record(inputName)}
        />

        {/* Fake submit - UX click magnet; not needed, just for blurring the input */}
        {isTitleEditable && (
          <Button
            type="button"
            size="sm"
            color="secondary"
            tabIndex={-1}
            onClick={() => setIsFocused(false)}
            className={`${fakeSubmit.animation} ${fakeSubmit.common} ${isFocused ? fakeSubmit.visible : fakeSubmit.hidden}`}
            {...dataTestId('jobAddPage.jobActions.submitTitleButton')}
          >
            <ArrowRightIcon className="mr-px size-4 stroke-3" />
          </Button>
        )}
      </div>
    </Label>
  );
};
